/**
 * Assistente inteligente de buscas (porta de `getSuggestions`/`showSuggestions`
 * [application.js:9145-9338]):
 *  - GET /app/suggestions/:language → [{ title, parameters }] — buscas sugeridas prontas;
 *    `parameters` é uma query string (ex.: "module=emails&domains=[@gmail.com|Gmail;…]").
 *  - GET /app/keywords?module=…      → palavras-chave sugeridas para o campo (autocomplete).
 * Ambas mandam os headers de auth (id/email) + `language`, como o legado.
 */
import { useQuery } from '@tanstack/react-query';
import i18next from 'i18next';
import { api } from '../lib/apiClient';

function langHeader(): Record<string, string> {
  return { language: (i18next.language ?? 'pt').replace('-', '_') };
}

export interface SearchSuggestion {
  title: string;
  /** Query string com os parâmetros da busca (module/keywords/sites/domains/…). */
  parameters: string;
}

export function useSearchSuggestions(enabled: boolean) {
  const language = (i18next.language ?? 'pt').split('-')[0];
  return useQuery({
    queryKey: ['suggestions', language],
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<SearchSuggestion[]> => {
      const result = await api.get<SearchSuggestion[]>(`app/suggestions/${language}`, {
        headers: langHeader(),
        allowEmpty: true,
      });
      if (!result.ok) throw new Error(result.code);
      return Array.isArray(result.data) ? result.data : [];
    },
  });
}

export interface KeywordSuggestion {
  keyword: string;
}

/** Palavras-chave sugeridas para o campo do módulo (getSuggestions branch sem extensão). */
export async function fetchKeywordSuggestions(module: string): Promise<string[]> {
  const result = await api.get<KeywordSuggestion[]>(`app/keywords?module=${encodeURIComponent(module)}`, {
    headers: langHeader(),
    allowEmpty: true,
  });
  if (!result.ok) return [];
  return (Array.isArray(result.data) ? result.data : [])
    .map((r) => r.keyword)
    .filter((k): k is string => Boolean(k));
}
